import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { StatCounter } from '../shared/StatCounter';

/**
 * "The Double Crisis" section — ports the vanilla HTML crisis section to React.
 *
 * Two subsections:
 *   A) The Old Damage — 4 stat cards showing legacy education failures.
 *   B) The New Damage — 3 danger stat cards showing AI-induced cognitive atrophy.
 *
 * A single IntersectionObserver on the <section> drives all fade-up reveals;
 * CSS `--stagger` custom property handles per-element transition delay.
 */
export function CrisisSection() {
  const [sectionRef, isVisible] = useIntersectionObserver<HTMLElement>({ threshold: 0.1 });

  const reveal = (stagger?: number) =>
    `fade-up ${isVisible ? 'revealed' : ''}${stagger !== undefined ? '' : ''}`;

  const stagger = (i: number): React.CSSProperties =>
    ({ '--stagger': i }) as React.CSSProperties;

  return (
    <section
      id="crisis"
      className="section crisis-section"
      aria-label="The Double Crisis"
      ref={sectionRef}
    >
      <div className="section-inner">
        <h2 className={`section-title ${reveal()}`}>The Double Crisis</h2>
        <p className={`section-subtitle ${reveal()}`} style={stagger(1)}>
          The old model was already failing. AI just made it catastrophically worse.
        </p>

        {/* A) The Old Damage */}
        <div className="crisis-subsection">
          <h3 className={`crisis-heading ${reveal()}`}>The Old Damage</h3>
          <p className={`crisis-bridge ${reveal()}`}>
            The science of how people learn has existed for decades. Research takes an average of
            17&nbsp;years to reach practice. Education never caught up.
          </p>

          <div className="stat-grid" data-stagger>
            {/* Card 1 — Training transfer */}
            <div className={`stat-card ${reveal()}`} style={stagger(0)}>
              <md-elevation />
              <span className="material-symbols-outlined stat-icon">school</span>
              <StatCounter target={12} suffix="%" />
              <span className="stat-label">of employees apply training on the job</span>
              <span className="stat-source">
                <a href="https://scholarworks.wmich.edu/books/296/" target="_blank" rel="noopener">
                  Brinkerhoff, 2006
                </a>
              </span>
            </div>

            {/* Card 2 — MOOC completion */}
            <div className={`stat-card ${reveal()}`} style={stagger(1)}>
              <md-elevation />
              <span className="material-symbols-outlined stat-icon">trending_down</span>
              <StatCounter target={5} suffix="%" display="5–15%" />
              <span className="stat-label">MOOC completion rates worldwide</span>
              <span className="stat-source">
                <a
                  href="https://www.irrodl.org/index.php/irrodl/article/view/2112"
                  target="_blank"
                  rel="noopener"
                >
                  Jordan, 2015
                </a>
                ;{' '}
                <a
                  href="https://www.classcentral.com/report/mooc-stats-2019/"
                  target="_blank"
                  rel="noopener"
                >
                  Shah, 2019
                </a>
              </span>
            </div>

            {/* Card 3 — Corporate training spend */}
            <div className={`stat-card ${reveal()}`} style={stagger(2)}>
              <md-elevation />
              <span className="material-symbols-outlined stat-icon">payments</span>
              <StatCounter target={350} prefix="$" suffix="B" />
              <span className="stat-label">
                spent annually on corporate training with little measurement of impact
              </span>
              <span className="stat-source">
                <a
                  href="https://hbr.org/2019/10/where-companies-go-wrong-with-learning-and-development"
                  target="_blank"
                  rel="noopener"
                >
                  HBR, 2019
                </a>
                ;{' '}
                <a
                  href="https://joshbersin.com/2024/03/the-340-billion-corporate-learning-industry-is-poised-for-disruption/"
                  target="_blank"
                  rel="noopener"
                >
                  Bersin, 2024
                </a>
              </span>
            </div>

            {/* Card 4 — Skills disruption */}
            <div className={`stat-card ${reveal()}`} style={stagger(3)}>
              <md-elevation />
              <span className="material-symbols-outlined stat-icon">update</span>
              <StatCounter target={44} suffix="%" />
              <span className="stat-label">
                of workers&apos; core skills will be disrupted by 2027
              </span>
              <span className="stat-source">
                <a
                  href="https://www.weforum.org/publications/the-future-of-jobs-report-2023/in-full/4-skills-outlook/"
                  target="_blank"
                  rel="noopener"
                >
                  World Economic Forum, 2023
                </a>
              </span>
            </div>
          </div>
        </div>

        {/* B) The New Damage */}
        <div className="crisis-subsection">
          <h3 className={`crisis-heading crisis-heading-danger ${reveal()}`}>
            The New Damage: AI-Induced Cognitive Atrophy
          </h3>

          <div className="stat-grid" data-stagger>
            {/* Danger Card 1 — Brain engagement */}
            <div className={`stat-card stat-card-danger ${reveal()}`} style={stagger(0)}>
              <md-elevation />
              <span className="material-symbols-outlined stat-icon">psychology</span>
              <StatCounter target={0} display="LOWEST" className="stat-number-text" />
              <span className="stat-label">
                Brain engagement in ChatGPT users during learning tasks
              </span>
              <span className="stat-source">
                <a href="https://arxiv.org/abs/2506.08872" target="_blank" rel="noopener">
                  MIT Media Lab, 2025
                </a>
              </span>
            </div>

            {/* Danger Card 2 — Critical thinking */}
            <div className={`stat-card stat-card-danger ${reveal()}`} style={stagger(1)}>
              <md-elevation />
              <span className="material-symbols-outlined stat-icon">trending_down</span>
              <StatCounter target={666} />
              <span className="stat-label">
                participants confirmed: AI usage negatively correlates with critical thinking
              </span>
              <span className="stat-source">
                <a
                  href="https://www.mdpi.com/2075-4698/15/1/6"
                  target="_blank"
                  rel="noopener"
                >
                  Gerlich, 2025
                </a>
              </span>
            </div>

            {/* Danger Card 3 — Enterprise AI failure */}
            <div className={`stat-card stat-card-danger ${reveal()}`} style={stagger(2)}>
              <md-elevation />
              <span className="material-symbols-outlined stat-icon">dangerous</span>
              <StatCounter target={95} suffix="%" />
              <span className="stat-label">
                of enterprise GenAI pilots fail to deliver measurable business impact
              </span>
              <span className="stat-source">
                <a
                  href="https://fortune.com/2025/08/18/mit-report-95-percent-generative-ai-pilots-at-companies-failing-cfo/"
                  target="_blank"
                  rel="noopener"
                >
                  MIT NANDA Report, 2025
                </a>
              </span>
            </div>
          </div>
        </div>

        <p className={`crisis-summary ${reveal()}`}>
          Everyone is throwing AI at education without understanding how humans learn. The result?{' '}
          <strong>AI-Induced Cognitive Atrophy.</strong> Students outsource their thinking. Memory
          degrades. Critical thinking declines. The tool meant to help is creating a generation of
          intellectual dependents.
        </p>
      </div>
    </section>
  );
}
